<template>
  <div v-if="route">
    <component
      v-if="user && isAuthorized && resolvedComponent"
      :is="resolvedComponent"
      v-bind="route.params"
      go-back
    />
    <LogIn
      v-else-if="user && !isAuthorized"
      :value="true"
      :title="$t('error.accessDenied')"
      color="admin"
    />
  </div>
</template>

<script>
import { defineAsyncComponent, markRaw } from "vue";
import LogIn from "../../dialogs/LogIn";

export default {
  name: "dialog-router-view",
  components: { LogIn },
  props: ["auth"],
  data() {
    return {
      route: null,
      resolvedComponent: null,
      loadError: null,
      authKey: 0, // Increment to force re-evaluation
    };
  },
  computed: {
    isAuthorized() {
      // Access authKey to create dependency for forced re-evaluation
      this.authKey;

      const user = this.user;
      const routeAuth = this.route?.meta?.auth;

      // If no auth function defined for this route, allow access
      if (!routeAuth) return true;

      // Call auth function with current user and route params
      return routeAuth(this.$dialog.params, user);
    },
    user() {
      return this.$store.state.main.user;
    },
  },
  watch: {
    user(newUser) {
      // Force re-evaluation of isAuthorized when user loads
      if (newUser && this.route) {
        this.authKey++;
      }
    },
  },
  methods: {
    async navigate(routeOrEvent) {
      // Handle both route object (from afterEach) and hashchange event
      const hash = routeOrEvent?.hash ?? window.location.hash;

      // Deduplicate: skip if hash hasn't changed since last navigate
      if (hash === this._lastHash) return;
      this._lastHash = hash;

      const previousName = this.route?.name;
      this.$dialogs.currentRoute = this.$dialogs.match(hash.slice(1));

      // Read the current dialog route (reactive update)
      this.route = this.$dialog;

      // Same-component, params-only change: keep the existing dialog
      // component instance alive and let `v-bind="route.params"` push the
      // new params through reactively. Without this, every $dialogs.replace
      // (e.g., expanding a section on ContactView, which updates the
      // section route param) would unmount + remount the dialog
      // component — user-visibly closing and re-opening the dialog.
      if (
        this.route?.name &&
        this.route.name === previousName &&
        this.resolvedComponent
      ) {
        return;
      }

      // Different route (or first nav) — tear down and re-resolve.
      this.resolvedComponent = null;
      this.loadError = null;

      // Increment navigation ID to detect stale async resolutions
      const navId = ++this._navId;

      // Resolve async component if route has one
      const route = this.route;
      if (route?.component && typeof route.component === "function") {
        try {
          const module = await route.component();
          // Only apply if this is still the current navigation
          if (navId !== this._navId) return;
          // markRaw prevents Vue from making the component reactive (performance)
          this.resolvedComponent = markRaw(module.default || module);
        } catch (e) {
          if (navId !== this._navId) return;
          console.error("Failed to load dialog component:", e);
          this.loadError = e.message;
          this.resolvedComponent = null;
        }
      } else if (route?.component) {
        this.resolvedComponent = markRaw(route.component);
      }
    },
  },
  created() {
    this._navId = 0;
    this._lastHash = null;
    this.$router.afterEach(this.navigate);
    // Vue Router 4 doesn't trigger afterEach for hash-only changes
    // Add hashchange listener for dialog routing
    this._hashChangeHandler = () => this.navigate();
    window.addEventListener("hashchange", this._hashChangeHandler);
    this.navigate(this.$route);
  },
  beforeUnmount() {
    window.removeEventListener("hashchange", this._hashChangeHandler);
  },
};
</script>
